import React, { useState, useRef } from 'react';
import { 
  Send, 
  ImageIcon, 
  X, 
  Loader2, 
  BarChart3, 
  Calendar, 
  Plus, 
  Bold, 
  Italic, 
  List, 
  Code, 
  Link as LinkIcon, 
  GraduationCap 
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface NewMessageFormProps {
  onSubmit: (content: string, categoryOrImage?: string | File) => Promise<boolean>;
  onCreatePoll?: (question: string, options: string[], expiresAt?: Date) => Promise<boolean>;
  isAdmin?: boolean;
  selectedCategory?: string | null;
}

interface Class {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
}

interface Blueprint {
  id: string;
  title: string;
  description: string;
  logo_url: string | null;
}

interface AITool {
  id: string;
  title: string;
  description: string;
  logo_url: string | null;
}

export function NewMessageForm({ onSubmit, onCreatePoll, isAdmin, selectedCategory }: NewMessageFormProps) {
  const [newMessage, setNewMessage] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [showClassPalette, setShowClassPalette] = useState(false);
  const [showBlueprintPalette, setShowBlueprintPalette] = useState(false);
  const [showAIToolPalette, setShowAIToolPalette] = useState(false);
  const [classes, setClasses] = useState<Class[]>([]);
  const [blueprints, setBlueprints] = useState<Blueprint[]>([]);
  const [aiTools, setAITools] = useState<AITool[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingBlueprints, setLoadingBlueprints] = useState(false);
  const [loadingAITools, setLoadingAITools] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showPollModal, setShowPollModal] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState<string[]>(['', '']);
  const [pollExpiresAt, setPollExpiresAt] = useState<string>('');
  const [isCreatingPoll, setIsCreatingPoll] = useState(false);
  
  // Detect text selection for bubble menu
  const [selection, setSelection] = useState<{
    isActive: boolean;
    top: number;
    left: number;
  }>({ isActive: false, top: 0, left: 0 });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Track if formatting button was clicked to prevent toolbar from disappearing
  const [formattingButtonClicked, setFormattingButtonClicked] = useState(false);
  
  // Auto-resize textarea as content grows
  const adjustTextareaHeight = (element: HTMLTextAreaElement | null, extraPadding: number = 0) => {
    if (!element) return;
    
    // Reset height to auto to get the correct scrollHeight
    element.style.height = 'auto';
    
    // Set the height to match content (with a minimum)
    const minHeight = 56; // ~2 rows
    const maxHeight = 200; // Maximum height before scrolling
    const newHeight = Math.min(Math.max(element.scrollHeight + extraPadding, minHeight), maxHeight);
    element.style.height = `${newHeight}px`;
  };
  
  // Honor reduced motion preferences
  const prefersReducedMotion = typeof window !== 'undefined' ? 
    window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;

  const fetchClasses = async () => {
    setLoadingClasses(true);
    try {
      const { data, error } = await supabase
        .from('classes')
        .select('id, title, description, thumbnail, video_url')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setClasses(data || []);
    } catch (err) {
      console.error('Error fetching classes:', err);
    } finally {
      setLoadingClasses(false);
    }
  };

  const fetchBlueprints = async () => {
    setLoadingBlueprints(true);
    try {
      const { data, error } = await supabase
        .from('blueprints')
        .select('id, title, description, logo_url')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBlueprints(data || []);
    } catch (err) {
      console.error('Error fetching blueprints:', err);
    } finally {
      setLoadingBlueprints(false);
    }
  };

  const fetchAITools = async () => {
    setLoadingAITools(true);
    try {
      const { data, error } = await supabase
        .from('ai_tools')
        .select('id, title, description, logo_url')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAITools(data || []);
    } catch (err) {
      console.error('Error fetching AI tools:', err);
    } finally {
      setLoadingAITools(false);
    }
  };

  const handleClassSelect = async (classItem: Class) => {
    const url = `${window.location.origin}/classroom/${classItem.id}`;
    const message = `**${classItem.title}**\n${classItem.description}\n\n${url}`;
    await onSubmit(message);
    setNewMessage('');
    setShowClassPalette(false);
  };

  const handleBlueprintSelect = async (blueprint: Blueprint) => {
    const url = `${window.location.origin}/blueprints/${blueprint.id}`;
    const message = `**${blueprint.title}**\n${blueprint.description}\n\n${url}`;
    await onSubmit(message);
    setNewMessage('');
    setShowBlueprintPalette(false);
  };

  const handleAIToolSelect = async (aiTool: AITool) => {
    const url = `${window.location.origin}/ai-tool/${aiTool.id}`;
    const message = `**${aiTool.title}**\n${aiTool.description}\n\n${url}`;
    await onSubmit(message);
    setNewMessage('');
    setShowAIToolPalette(false);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    // Only allow images under 10MB
    if (file.size > 10 * 1024 * 1024) {
      alert('Image size must be under 10MB');
      return;
    }
    
    setSelectedImage(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!newMessage.trim() && !selectedImage) || isSubmitting) return;
    
    setIsSubmitting(true);
    
    // Use the currently selected category when it's an image upload
    // If it's not an image, the selectedCategory will be passed by the parent
    const success = selectedImage
      ? await onSubmit(newMessage, selectedImage)
      : await onSubmit(newMessage, selectedCategory || undefined);
      
    if (success) {
      setNewMessage('');
      setSelectedImage(null);
    }
    setIsSubmitting(false);
  };

  const insertFormatting = (prefix: string, suffix: string = prefix) => {
    const textarea = document.getElementById('messageInput') as HTMLTextAreaElement;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    
    if (start === end) {
      // No selection, just insert the formatting
      const updatedText = `${text.substring(0, start)}${prefix}${suffix}${text.substring(end)}`;
      setNewMessage(updatedText);
      // Set cursor position between the prefix and suffix
      // Ensure textarea remains focused and keyboard stays open on mobile
      if (isMobile) {
        // For mobile, we need to be more aggressive about keeping focus
        textarea.focus();
        textarea.selectionStart = start + prefix.length;
        textarea.selectionEnd = start + prefix.length;
      } else {
        setTimeout(() => {
          textarea.focus();
          textarea.selectionStart = start + prefix.length;
          textarea.selectionEnd = start + prefix.length;
        }, 0);
      }
    } else {
      // Wrap the selection with formatting
      const updatedText = `${text.substring(0, start)}${prefix}${text.substring(start, end)}${suffix}${text.substring(end)}`;
      setNewMessage(updatedText);
      // Set cursor position after the formatted text
      if (isMobile) {
        // For mobile, we need to be more aggressive about keeping focus
        textarea.focus();
        textarea.selectionStart = end + prefix.length + suffix.length;
        textarea.selectionEnd = end + prefix.length + suffix.length;
      } else {
        setTimeout(() => {
          textarea.focus();
          textarea.selectionStart = end + prefix.length + suffix.length;
          textarea.selectionEnd = end + prefix.length + suffix.length;
        }, 0);
      }
    }
  };

  const handleCreatePoll = async () => {
    if (!onCreatePoll || !pollQuestion.trim() || pollOptions.some(opt => !opt.trim()) || isCreatingPoll) return;
    
    setIsCreatingPoll(true);
    try {
      // Convert date string to Date object if provided
      const expiresAt = pollExpiresAt ? new Date(pollExpiresAt) : undefined;
      // Filter out empty options
      const validOptions = pollOptions.filter(opt => opt.trim());
      
      const success = await onCreatePoll(pollQuestion, validOptions, expiresAt);
      if (success) {
        setPollQuestion('');
        setPollOptions(['', '']);
        setPollExpiresAt('');
        setShowPollModal(false);
      }
    } catch (error) {
      console.error('Error creating poll:', error);
    } finally {
      setIsCreatingPoll(false);
    }
  };

  const handleAddOption = () => {
    if (pollOptions.length < 5) {
      setPollOptions([...pollOptions, '']);
    }
  };

  const handleRemoveOption = (index: number) => {
    if (pollOptions.length > 2) {
      setPollOptions(pollOptions.filter((_, i) => i !== index));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // On mobile, Enter key always adds a new line
    // On desktop, Enter without Shift submits the message
    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey && !isMobile) {
      e.preventDefault();
      const form = e.currentTarget.closest('form');
      if (form) {
        form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    }
  };
  
  // Check if device is mobile
  const isMobile = typeof window !== 'undefined' ? 
    window.matchMedia('(max-width: 768px)').matches : false;
    
  // Track if the textarea is focused
  const [isTextareaFocused, setIsTextareaFocused] = useState(false);
  
  // Detect selection changes
  const handleSelectionChange = () => {
    if (!textareaRef.current) return;
    
    const textarea = textareaRef.current;
    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;
    
    // We used to update text selection state here
    
    if (selectionStart !== selectionEnd) {
      if (!isMobile) {
        // On desktop, calculate position based on cursor location
        const rect = textarea.getBoundingClientRect();
        const textBeforeSelection = textarea.value.substring(0, selectionStart);
        const lineBreaks = (textBeforeSelection.match(/\n/g) || []).length;
        
        // Calculate approximate position
        const lineHeight = 24; // Approximate line height in pixels
        const charWidth = 8;  // Approximate character width in pixels
        
        const top = rect.top + lineBreaks * lineHeight - 48; // Position above selection
        const left = rect.left + (textBeforeSelection.length - lineBreaks) * charWidth;
        
        setSelection({ isActive: true, top, left });
      } else {
        // On mobile, we don't need position since we'll show inside textarea
        setSelection({ isActive: true, top: 0, left: 0 });
      }
    } else {
      setSelection({ isActive: false, top: 0, left: 0 });
    }
    
    // When text is selected, increase textarea height on mobile
    if (isMobile && selectionStart !== selectionEnd) {
      if (textareaRef.current) {
        // Make room for the formatting toolbar
        adjustTextareaHeight(textareaRef.current, 40); // Extra padding for toolbar
      }
    } else {
      adjustTextareaHeight(textareaRef.current);
    }
  };
  
  // Handle keyboard shortcuts for formatting
  const handleFormatKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Handle original keydown behavior
    handleKeyDown(e);
    
    // Check for formatting shortcuts
    if (e.metaKey || e.ctrlKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          insertFormatting('**');
          break;
        case 'i':
          e.preventDefault();
          insertFormatting('*');
          break;
        case 'k':
          e.preventDefault();
          insertFormatting('[', '](url)');
          break;
        case '`':
          e.preventDefault();
          insertFormatting('`');
          break;
      }
    }
  };
  
  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4 pt-1 pb-safe">
        <div className="flex flex-col gap-2">
          {/* Message input with floating controls */}
          <div className="relative group">
            <textarea
              id="messageInput"
              ref={textareaRef}
              value={newMessage}
              onChange={(e) => {
                setNewMessage(e.target.value);
                // Auto-adjust height
                adjustTextareaHeight(textareaRef.current);
                
                if (e.target.value === '/classes') {
                  setNewMessage('');
                  setShowBlueprintPalette(false);
                  setShowAIToolPalette(false);
                  setShowClassPalette(true);
                  fetchClasses();
                } else if (e.target.value === '/blueprints') {
                  setNewMessage('');
                  setShowClassPalette(false);
                  setShowBlueprintPalette(true);
                  setShowAIToolPalette(false);
                  fetchBlueprints();
                } else if (e.target.value === '/aitools') {
                  setNewMessage('');
                  setShowClassPalette(false);
                  setShowBlueprintPalette(false);
                  setShowAIToolPalette(true);
                  fetchAITools();
                } else if (!e.target.value.startsWith('/classes') && !e.target.value.startsWith('/blueprints') && !e.target.value.startsWith('/aitools')) {
                  setShowClassPalette(false);
                  setShowBlueprintPalette(false);
                  setShowAIToolPalette(false);
                }
              }}
              onKeyDown={handleFormatKeyDown}
              onSelect={handleSelectionChange}
              onFocus={() => setIsTextareaFocused(true)}
              onBlur={(e) => {
                // Check if a formatting button was clicked
                if (formattingButtonClicked) {
                  // If a formatting button was clicked, keep the toolbar visible
                  setTimeout(() => {
                    if (textareaRef.current) {
                      textareaRef.current.focus();
                      setIsTextareaFocused(true);
                    }
                    // Reset the flag after handling
                    setFormattingButtonClicked(false);
                  }, 10);
                  return;
                }
                
                // Check if the blur is due to clicking on a formatting button element itself
                const relatedTarget = e.relatedTarget as HTMLElement;
                const isFormattingButton = relatedTarget && 
                  (relatedTarget.closest('.formatting-toolbar') || 
                   relatedTarget.getAttribute('aria-label') === 'Bold' ||
                   relatedTarget.getAttribute('aria-label') === 'Italic' ||
                   relatedTarget.getAttribute('aria-label') === 'Inline Code' ||
                   relatedTarget.getAttribute('aria-label') === 'List');
                
                if (isFormattingButton) {
                  // Don't hide toolbar if a formatting button was pressed
                  // Re-focus the textarea to keep the keyboard open
                  setTimeout(() => {
                    if (textareaRef.current) {
                      textareaRef.current.focus();
                      setIsTextareaFocused(true);
                    }
                  }, 10);
                  return;
                }
                
                // Otherwise hide the toolbar normally
                setTimeout(() => {
                  setSelection({ isActive: false, top: 0, left: 0 });
                  setIsTextareaFocused(false);
                }, 150);
              }}
              onClick={handleSelectionChange}
              placeholder="Type your message..."
              rows={1}
              className={`w-full bg-gray-800 text-white rounded-lg px-4 ${isMobile && isTextareaFocused ? 'pt-12 pb-3' : 'py-3'} pr-20 focus:outline-none focus:ring-2 focus:ring-[#c9fffc] resize-none overflow-hidden ${isMobile && isTextareaFocused ? 'min-h-[120px]' : 'min-h-[56px]'} transition-all duration-200`}
            />
            
            {/* Mobile formatting toolbar inside textarea - visible when focused */}
            {isMobile && isTextareaFocused && (
              <div 
                className="formatting-toolbar absolute top-0 left-0 right-0 bg-[#151D29]/90 backdrop-blur-md border-b border-[#c9fffc]/20 flex items-center justify-center p-1 z-10"
                onMouseDown={(e) => {
                  // Prevent mousedown from removing focus from textarea
                  e.preventDefault();
                }}
                onClick={(e) => {
                  // Prevent default click behavior
                  e.stopPropagation();
                  // Ensure textarea stays focused
                  if (textareaRef.current) {
                    textareaRef.current.focus();
                  }
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setFormattingButtonClicked(true);
                    insertFormatting('**');
                    // Keep the formatting toolbar visible
                    setIsTextareaFocused(true);
                  }}
                  className="p-1.5 text-gray-400 hover:text-[#c9fffc] rounded-md transition-colors"
                  aria-label="Bold"
                >
                  <Bold className="w-4 h-4 opacity-80 hover:opacity-100" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFormattingButtonClicked(true);
                    insertFormatting('*');
                    // Keep the formatting toolbar visible
                    setIsTextareaFocused(true);
                  }}
                  className="p-1.5 text-gray-400 hover:text-[#c9fffc] rounded-md transition-colors"
                  aria-label="Italic"
                >
                  <Italic className="w-4 h-4 opacity-80 hover:opacity-100" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFormattingButtonClicked(true);
                    insertFormatting('`');
                    // Keep the formatting toolbar visible
                    setIsTextareaFocused(true);
                  }}
                  className="p-1.5 text-gray-400 hover:text-[#c9fffc] rounded-md transition-colors"
                  aria-label="Inline Code"
                >
                  <Code className="w-4 h-4 opacity-80 hover:opacity-100" />
                </button>
                <button
                  type="button"
                  onClick={() => insertFormatting('- ')}
                  className="p-1.5 text-gray-400 hover:text-[#c9fffc] rounded-md transition-colors"
                  aria-label="Bullet List"
                >
                  <List className="w-4 h-4 opacity-80 hover:opacity-100" />
                </button>
                <button
                  type="button"
                  onClick={() => insertFormatting('[', '](url)')}
                  className="p-1.5 text-gray-400 hover:text-[#c9fffc] rounded-md transition-colors"
                  aria-label="Insert Link"
                >
                  <LinkIcon className="w-4 h-4 opacity-80 hover:opacity-100" />
                </button>
              </div>
            )}
            
            {/* Button group (right aligned) */}
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
              {/* Upload button */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="opacity-60 hover:opacity-100 focus:opacity-100 text-white transition-opacity duration-200 ease-in-out p-1.5"
                aria-label="Upload image"
              >
                <ImageIcon className="w-5 h-5" />
              </button>
              
              {/* Admin-only poll button */}
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => setShowPollModal(true)}
                  className="opacity-60 hover:opacity-100 focus:opacity-100 transition-opacity duration-200 ease-in-out p-1.5"
                  aria-label="Create poll"
                >
                  <BarChart3 className="w-5 h-5 text-[#c9fffc]/80" />
                </button>
              )}
              
              {/* Submit button */}
              <button
                type="submit"
                disabled={isSubmitting || (!newMessage.trim() && !selectedImage)}
                className="text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed p-1.5"
                aria-label="Send message"
              >
                {isSubmitting ? (
                  <Loader2 className="w-5 h-5 animate-spin text-[#c9fffc]" />
                ) : (
                  <Send className="w-5 h-5 text-[#c9fffc]" />
                )}
              </button>
            </div>
            
            {/* Formatting bubble menu that appears on text selection (desktop only) */}
            {!isMobile && selection.isActive && (
              <div 
                className="fixed z-50 bg-[#151D29]/90 backdrop-blur-md border border-[#c9fffc]/20 rounded-lg shadow-lg flex items-center gap-1 p-1 transition-all duration-150"
                style={{
                  top: `${selection.top}px`,
                  left: `${selection.left}px`,
                  transform: 'translateX(-50%)',
                }}
              >
                <button
                  type="button"
                  onClick={() => insertFormatting('**')}
                  className="p-1.5 text-gray-400 hover:text-[#c9fffc] rounded-md transition-colors"
                  title="Bold (⌘B)"
                  aria-label="Bold"
                >
                  <Bold className="w-5 h-5 opacity-80 hover:opacity-100" />
                </button>
                <button
                  type="button"
                  onClick={() => insertFormatting('*')}
                  className="p-1.5 text-gray-400 hover:text-[#c9fffc] rounded-md transition-colors"
                  title="Italic (⌘I)"
                  aria-label="Italic"
                >
                  <Italic className="w-5 h-5 opacity-80 hover:opacity-100" />
                </button>
                <button
                  type="button"
                  onClick={() => insertFormatting('`')}
                  className="p-1.5 text-gray-400 hover:text-[#c9fffc] rounded-md transition-colors"
                  title="Code (⌘`)"
                  aria-label="Inline Code"
                >
                  <Code className="w-5 h-5 opacity-80 hover:opacity-100" />
                </button>
                <button
                  type="button"
                  onClick={() => insertFormatting('- ')}
                  className="p-1.5 text-gray-400 hover:text-[#c9fffc] rounded-md transition-colors"
                  title="Bullet List"
                  aria-label="Bullet List"
                >
                  <List className="w-5 h-5 opacity-80 hover:opacity-100" />
                </button>
                <button
                  type="button"
                  onClick={() => insertFormatting('[', '](url)')}
                  className="p-1.5 text-gray-400 hover:text-[#c9fffc] rounded-md transition-colors"
                  title="Link (⌘K)"
                  aria-label="Insert Link"
                >
                  <LinkIcon className="w-5 h-5 opacity-80 hover:opacity-100" />
                </button>
              </div>
            )}
          </div>
          
          {/* Image preview */}
          {selectedImage && (
            <div className="flex items-center gap-2 bg-[#151D29]/60 backdrop-blur-sm border border-[#c9fffc]/10 rounded-lg p-2 mt-2">
              <div className="w-14 h-14 flex-shrink-0 overflow-hidden rounded-md bg-gray-700 flex items-center justify-center">
                <img 
                  src={URL.createObjectURL(selectedImage)} 
                  alt="Preview" 
                  className="w-full h-full object-cover" 
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-200 truncate">{selectedImage.name}</p>
                <p className="text-xs text-gray-400">{(selectedImage.size / 1024).toFixed(1)} KB</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedImage(null)}
                className="p-1 text-gray-400 hover:text-white rounded-full hover:bg-red-500/10"
                aria-label="Remove image"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageSelect}
          ref={fileInputRef}
          className="hidden"
        />
      </form>

      {/* Class palette */}
      {showClassPalette && (
        <div className="absolute left-0 right-0 bottom-[calc(100%+0.5rem)] bg-gray-800 rounded-lg shadow-lg border border-gray-700 max-h-80 overflow-y-auto z-50">
          <div className="p-2 border-b border-gray-700 flex items-center justify-between text-[#c9fffc]">
            <div className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5" />
              <span className="font-medium">Available Classes</span>
            </div>
            <button
              onClick={() => setShowClassPalette(false)}
              className="p-1 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {loadingClasses ? (
            <div className="p-4 text-center">
              <Loader2 className="w-6 h-6 animate-spin text-[#c9fffc] mx-auto" />
            </div>
          ) : classes.length === 0 ? (
            <div className="p-4 text-center text-gray-400">
              No classes found
            </div>
          ) : (
            <div className="p-2 space-y-2">
              {classes.map((classItem) => (
                <button
                  key={classItem.id}
                  onClick={() => handleClassSelect(classItem)}
                  className="w-full bg-gray-750 hover:bg-gray-700 transition-colors rounded-lg p-3 text-left flex items-start gap-3"
                >
                  <div className="w-8 h-8 bg-gray-800 rounded-lg flex-shrink-0 flex items-center justify-center">
                    {classItem.thumbnail ? (
                      <img
                        src={classItem.thumbnail}
                        alt={classItem.title}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <GraduationCap className="w-5 h-5 text-[#c9fffc]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium mb-1 truncate">{classItem.title}</h3>
                    <p className="text-gray-400 text-xs line-clamp-2">{classItem.description}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Poll Modal */}
      {showPollModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">Create New Poll</h2>
              <button
                onClick={() => setShowPollModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Question
                </label>
                <input
                  type="text"
                  value={pollQuestion}
                  onChange={(e) => setPollQuestion(e.target.value)}
                  placeholder="What's your question?"
                  className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Options
                </label>
                <div className="space-y-2">
                  {pollOptions.map((option, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...pollOptions];
                          newOptions[index] = e.target.value;
                          setPollOptions(newOptions);
                        }}
                        placeholder={`Option ${index + 1}`}
                        className="flex-1 bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc]"
                      />
                      {pollOptions.length > 2 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveOption(index)}
                          className="p-2 text-red-400 hover:text-red-300"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {pollOptions.length < 5 && (
                  <button
                    type="button"
                    onClick={handleAddOption}
                    className="mt-2 text-sm text-[#c9fffc] hover:text-white transition-colors"
                  >
                    <Plus className="w-4 h-4 inline-block mr-1" />
                    Add Option
                  </button>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Expires At (Optional)
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="datetime-local"
                    value={pollExpiresAt}
                    onChange={(e) => setPollExpiresAt(e.target.value)}
                    className="w-full bg-gray-700 text-white rounded-lg pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#c9fffc]"
                  />
                </div>
              </div>

              <button
                onClick={handleCreatePoll}
                disabled={isCreatingPoll || !pollQuestion.trim() || pollOptions.some(opt => !opt.trim())}
                className="w-full bg-[#c9fffc] text-gray-900 rounded-lg px-4 py-2 font-medium hover:bg-[#a0fcf9] focus:outline-none focus:ring-2 focus:ring-[#c9fffc] disabled:opacity-50"
              >
                {isCreatingPoll ? (
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                ) : (
                  'Create Poll'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
